import { ITenant } from "~/types/tenant"
import { IUser } from "~/types/user"

import { isValidEnvironment } from "./helpers"
import { getVisibleOsModules } from "./os-modules"

let identified = false
let retryTimer: ReturnType<typeof setTimeout> | null = null
let retryAttempts = 0

function toUnixMs(value: string | undefined | null): number | undefined {
  if (!value) return undefined
  const ms = Date.parse(value)
  return Number.isNaN(ms) ? undefined : ms
}

export function userGuidingIdentify({
  user,
  tenant
}: {
  user: IUser
  tenant: ITenant | null
}) {
  if (typeof window === "undefined") return
  if (!window.userGuiding) {
    if (retryAttempts < 10 && !retryTimer) {
      retryTimer = setTimeout(() => {
        retryTimer = null
        retryAttempts++
        userGuidingIdentify({ user, tenant })
      }, 200)
    }
    return
  }

  if (retryTimer) {
    clearTimeout(retryTimer)
    retryTimer = null
  }
  retryAttempts = 0

  if (
    !isValidEnvironment ||
    window.location.href.includes("dev.orbitshift.ai")
  ) {
    return
  }
  if (identified) return
  if (!user?.id) return

  const isOsLite = !!tenant?.tenant_type?.includes("os_lite")
  const visibleOsModules = getVisibleOsModules(user.feature_flags, isOsLite)
  const name = `${user.fname || ""} ${user.sname || ""}`.trim() || "user-name"

  const attributes: Record<string, unknown> = {
    email: user.email,
    name,
    role: user.role,
    onboarded: user.onboarded ?? false,
    is_os_lite_user: isOsLite,
    created_at: toUnixMs(user.activated_at),
    last_login_at: toUnixMs(user.last_login_at),
    linked_tenants_count: user.linked_tenants?.length ?? 0,
    visible_os_modules: visibleOsModules,
    has_account_os: visibleOsModules.includes("accountOS"),
    has_rfp_os: visibleOsModules.includes("rfpOS"),
    has_marketing_os: visibleOsModules.includes("marketingOS"),
    has_agent_os: visibleOsModules.includes("agentOS")
  }

  if (user.tenant_id) {
    attributes.company = {
      id: String(user.tenant_id),
      name: user.tenant_name || tenant?.name
    }
  }

  try {
    window.userGuiding.identify(String(user.id), attributes)
    identified = true
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_) {
    // UserGuiding may be blocked (ad blocker) — fail silently
  }
}

export function userGuidingReset() {
  identified = false
  retryAttempts = 0
  if (retryTimer) {
    clearTimeout(retryTimer)
    retryTimer = null
  }
  if (typeof window === "undefined" || !window.userGuiding) return
  try {
    window.userGuiding.reset?.()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_) {
    // ignore
  }
}
